import { createContext, useContext, type ReactNode } from 'react';
import { useNotificationsState } from './useNotifications';
import type { UseNotificationsResult } from './useNotifications';
import type { UseNotificationsOptions } from './types';

/**
 * Holds the single notification state tree for an app. Undefined until a
 * `NotificationsProvider` is mounted above the consumer.
 */
const NotificationsContext = createContext<UseNotificationsResult | undefined>(
  undefined
);

export interface NotificationsProviderProps extends UseNotificationsOptions {
  children: ReactNode;
}

/**
 * App-wide notifications boundary. Mount this once, high in the tree (above the
 * router/pages) so the state + SignalR connection persist across page
 * navigation and never refetch on route changes. All notification UI
 * (`NotificationsBell`, custom badges, lists) reads from here via
 * `useNotifications()`.
 */
export const NotificationsProvider = ({
  children,
  ...options
}: NotificationsProviderProps) => {
  const value = useNotificationsState(options);
  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

/**
 * Reads the app's notification state from the nearest `NotificationsProvider`.
 * This is a pure consumer -- it performs no fetching or SignalR work itself.
 */
export const useNotifications = (): UseNotificationsResult => {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    throw new Error(
      'useNotifications must be used within a <NotificationsProvider>.'
    );
  }
  return ctx;
};
